import { confirmMinersChanges } from "@components/global/ConfirmMinersChanges";
import {
  AdminChangeRecord,
  AdminChangeSettingsRequest,
  AdminParamSettingsI,
  UserChangeSettingsRequest,
  UserParamSettingsI,
} from "@services/changeMinerSettings";
import {
  DeleteContractFromMiners,
  RemovePlanFromMiner,
} from "@services/contracts";
import { RecordResponse } from "@services/http/responses/BaseResponse";
import useApiRequests from "@services/http/useApiRequest";
import extendRecord from "@utils/extendRecord";
import { getFormValues } from "@utils/getFormValue";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { useSelectedMiners } from "@utils/hooks/useSelectedMiners";
import { useUsers } from "@utils/hooks/useUsers";
import { makeNotifs } from "@utils/makeNotifs";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useMe } from "../../../utils/hooks/useMe";
import { MyFormMinerSettings } from "./MinerRow/MinerRowMore/MyForm";

interface RequestResponse {
  response: void | RecordResponse<AdminChangeRecord[]>;
  params: { [key: string]: string };
}

export default function useSettingsChange(singleMinerId?: string) {
  const { onLoader, onSuccess } = useGlobalNotifs();
  const { enqueue } = useApiRequests();
  const { ok } = makeNotifs();
  const me = useMe();
  const dispatch = useDispatch();
  const selectedMiners = useSelectedMiners();
  const users = useUsers();

  const [changingSetting, setChangingSetting] = useState<string | undefined>();

  const sendRequestWithParams = async (params: {
    [key: string]: string;
  }): Promise<RequestResponse> => {
    const adminReq = new AdminChangeSettingsRequest(
      params as any as AdminParamSettingsI
    );
    const userReq = new UserChangeSettingsRequest(
      params as any as UserParamSettingsI
    );
    const request = me?.is_admin ? adminReq : userReq;
    const response = await enqueue<RecordResponse<AdminChangeRecord[]>>(
      request,
      { onLoadingStatusChanged: onLoader }
    );
    if (!!response) {
      onSuccess(ok.success);
    }
    return {
      response,
      params,
    };
  };

  const addMessage = async (e: React.FormEvent<MyFormMinerSettings>) => {
    const params = getFormValues(e.currentTarget.elements);

    const result = await onChange(params);
    if (result) {
      return {
        id: result?.Record ? result.Record[0].note_id : 0,
        date: Math.floor(Date.now() / 1000),
        text: params.admin_note,
      };
    }
    return null;
  };

  const saveNote = async (
    e: React.FormEvent<MyFormMinerSettings>,
    noteType: "admin_note" | "user_note"
  ) => {
    const params = getFormValues(e.currentTarget.elements);
    const result = await onChange(params);
    if (result) {
      return params[noteType];
    }
    return null;
  };

  const onChange = async (params: { [key: string]: string | number }) => {
    const miner = singleMinerId
      ? [singleMinerId]
      : selectedMiners.length > 1
        ? selectedMiners.map((m) => m.id)
        : [singleMinerId];

    if (selectedMiners.length > 5) {
      let props = params;
      if (params.hasOwnProperty("customer")) {
        const customer =
          users.find((u) => u.id == params["customer"])?.email || "unknown";
        props = {
          ...params,
          customer,
        };
      }
      if (
        !(await confirmMinersChanges(
          dispatch,
          extendRecord(props),
          selectedMiners
        ))
      )
        return;
    }

    const result = await sendRequestWithParams({
      ...params,
      miner: miner.join(","),
    });
    if (!result.response) return;
    return result.response;
  };

  const deleteContractFromMiner = async (miner: string) => {
    const res = await enqueue(new DeleteContractFromMiners(miner));
    if (!res) return;
    onSuccess(ok.success);
  };

  const removePlanFromMiner = async (miner: string) => {
    const response = await enqueue(new RemovePlanFromMiner(miner));
    if (!response) return;
    onSuccess(ok.success);
  };

  return {
    singleSetting: changingSetting,

    addMessage,
    saveNote,
    onChange,
    deleteContractFromMiner,
    removePlanFromMiner,
  };
}
